import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslocoService} from '@jsverse/transloco';
import {map} from 'rxjs';

import {AnimalSheltersRepository} from 'src/app/shared/states/animal-shelter.repository';
import {StatisticsResponse} from '../../models/statistics.model';
import {
  buildCategoryRows,
  formatDate,
  isEmptyFigures,
  StatisticsCategoryRow,
  stockEndLabel,
  stockStartLabel
} from '../../models/statistics-view.model';
import {StatisticsService} from '../../services/statistics.service';

type StatisticsTab = 'overview' | 'report';

/** Wie viele Jahre gleichzeitig in der Jahresleiste stehen. */
const YEAR_WINDOW_SIZE = 7;

/**
 * Wie viele Jahre der Verlauf zeigt.
 * `availableYears` reicht bis 2000 zurueck - alle zu laden waere ein Abruf je
 * Jahr, nur fuer ein Balkendiagramm.
 */
const TREND_YEAR_COUNT = 6;

@Component({
  selector: 'app-statistics-page',
  standalone: false,
  templateUrl: './statistics-page.component.html',
  styleUrls: ['./statistics-page.component.scss'],
  providers: [StatisticsService]
})
export class StatisticsPageComponent implements OnInit {
  response: StatisticsResponse | null = null;
  /** Vorjahr fuer den Vergleich, null solange nicht geladen oder nicht vorhanden. */
  previousResponse: StatisticsResponse | null = null;
  /** Endbestand je Jahr fuer den Verlauf. */
  trend: { year: number; stock: number; isCurrentYear: boolean }[] = [];

  rows: StatisticsCategoryRow[] = [];

  loading = true;
  trendLoading = false;
  loadFailed = false;

  activeTab: StatisticsTab = 'overview';

  /** Platzhalterzeilen waehrend des Ladens. */
  readonly skeletonLines = [1, 2, 3, 4, 5, 6];

  /** Alle verfuegbaren Jahre, aufsteigend. */
  years: number[] = [];
  /** Index des ersten Jahres der sichtbaren Leiste. */
  yearWindowStart = 0;

  animalShelterOptions: { label: string; value: number | null }[] = [];

  selectedYear: number = new Date().getFullYear();
  selectedShelterId: number | null = null;

  /** Anzeigeoptionen der Tiergruppen-Tabelle. */
  sortBySize = true;
  hideEmptyCategories = false;
  compareWithPreviousYear = false;

  /** Bereits geladene Jahre, speist Verlauf und Vorjahresvergleich. */
  private readonly yearData = new Map<number, StatisticsResponse>();

  private readonly _destroyRef = inject(DestroyRef);

  constructor(
    private readonly _statisticsService: StatisticsService,
    private readonly _animalSheltersRepository: AnimalSheltersRepository,
    private readonly _translocoService: TranslocoService
  ) {
  }

  ngOnInit(): void {
    this.selectedShelterId = this._animalSheltersRepository.getActiveAnimalShelter()?.id ?? null;

    this._animalSheltersRepository.getAnimalShelters().pipe(
      map(shelters => shelters.map(shelter => ({label: shelter.title, value: shelter.id as number | null}))),
      takeUntilDestroyed(this._destroyRef)
    ).subscribe(options => {
      this.animalShelterOptions = [
        {label: this._translocoService.translate('statistics.filter.allShelters'), value: null},
        ...options
      ];
    });

    this.loadStatistics();
  }

  onYearChange(year: number | null): void {
    if (year === null || year === this.selectedYear) {
      return;
    }
    this.selectedYear = year;
    this.loadStatistics();
  }

  /** Die sieben Jahre, die gerade in der Leiste stehen. */
  get visibleYears(): number[] {
    return this.years.slice(this.yearWindowStart, this.yearWindowStart + YEAR_WINDOW_SIZE);
  }

  get canPageBack(): boolean {
    return this.yearWindowStart > 0;
  }

  get canPageForward(): boolean {
    return this.yearWindowStart + YEAR_WINDOW_SIZE < this.years.length;
  }

  /** Blaettert die Jahresleiste, ohne das gewaehlte Jahr zu aendern. */
  pageYears(direction: -1 | 1): void {
    const maxStart = Math.max(0, this.years.length - YEAR_WINDOW_SIZE);
    const step = direction * YEAR_WINDOW_SIZE;
    this.yearWindowStart = Math.min(maxStart, Math.max(0, this.yearWindowStart + step));
  }

  /** Das laufende Jahr wird in der Leiste gekennzeichnet. */
  isRunningYear(year: number): boolean {
    return year === new Date().getFullYear();
  }

  onShelterChange(shelterId: number | null): void {
    this.selectedShelterId = shelterId;
    // Jedes Tierheim hat eigene Zahlen, der Jahres-Cache gilt nicht mehr.
    this._statisticsService.clear();
    this.yearData.clear();
    this.trend = [];
    this.previousResponse = null;
    this.loadStatistics();
  }

  onTabChange(tab: string | number | undefined): void {
    this.activeTab = tab === 'report' ? 'report' : 'overview';
  }

  print(): void {
    this.activeTab = 'report';
    // Der Bericht muss gerendert sein, bevor der Druckdialog den Baum einfriert.
    setTimeout(() => window.print());
  }

  get isCurrentYear(): boolean {
    return !!this.response?.isCurrentYear;
  }

  get isEmptyYear(): boolean {
    return !!this.response && isEmptyFigures(this.response.total);
  }

  /** Name des gewaehlten Tierheims, fuer den Kopf des Jahresberichts. */
  get shelterLabel(): string {
    const match = this.animalShelterOptions.find(option => option.value === this.selectedShelterId);
    return match?.label ?? this._translocoService.translate('statistics.filter.allShelters');
  }

  get periodLabel(): string {
    if (!this.response) {
      return '';
    }
    return `${formatDate(this.response.periodStart)} – ${formatDate(this.response.periodEnd)}`;
  }

  get periodEndLabel(): string {
    return this.response ? formatDate(this.response.periodEnd) : '';
  }

  get startLabel(): string {
    return this.response ? stockStartLabel(this.response) : '';
  }

  get endLabel(): string {
    return this.response ? stockEndLabel(this.response) : '';
  }

  private loadStatistics(): void {
    this.loading = true;
    this.loadFailed = false;

    this._statisticsService.load(this.selectedYear, this.selectedShelterId).pipe(
      takeUntilDestroyed(this._destroyRef)
    ).subscribe({
      next: response => {
        this.loading = false;
        if (!response) {
          this.loadFailed = true;
          return;
        }
        this.response = response;
        this.yearData.set(response.year, response);
        this.years = [...response.availableYears].sort((a, b) => a - b);
        this.centreYearWindow();
        this.previousResponse = this.yearData.get(this.selectedYear - 1) ?? null;
        this.applyResponse();
        this.loadRemainingYears(response);
      },
      error: () => {
        this.loading = false;
        this.loadFailed = true;
      }
    });
  }

  /** Jahresleiste so verschieben, dass das gewaehlte Jahr darin liegt. */
  private centreYearWindow(): void {
    const index = this.years.indexOf(this.selectedYear);
    if (index === -1) {
      this.yearWindowStart = Math.max(0, this.years.length - YEAR_WINDOW_SIZE);
      return;
    }
    const maxStart = Math.max(0, this.years.length - YEAR_WINDOW_SIZE);
    this.yearWindowStart = Math.min(maxStart, Math.max(0, index - Math.floor(YEAR_WINDOW_SIZE / 2)));
  }

  /** Die Jahre, die der Verlauf zeigt: das gewaehlte und die davor. */
  private trendYears(response: StatisticsResponse): number[] {
    return [...response.availableYears]
      .sort((a, b) => a - b)
      .filter(year => year <= response.year)
      .slice(-TREND_YEAR_COUNT);
  }

  /**
   * Holt die Jahre des Verlaufs nach dem ersten Rendern nach - sie speisen
   * Verlauf und Vorjahresvergleich, blockieren die Ansicht aber nicht.
   */
  private loadRemainingYears(response: StatisticsResponse): void {
    const wanted = this.trendYears(response);
    const missing = wanted.filter(year => !this.yearData.has(year));
    if (!missing.length) {
      this.applyYearData(response);
      return;
    }

    this.trendLoading = true;
    this._statisticsService.loadYears(missing, this.selectedShelterId).pipe(
      takeUntilDestroyed(this._destroyRef)
    ).subscribe({
      next: byYear => {
        this.trendLoading = false;
        byYear.forEach((entry, year) => this.yearData.set(year, entry));
        this.applyYearData(response);
      },
      error: () => {
        this.trendLoading = false;
      }
    });
  }

  /** Verlauf und Vorjahresvergleich aus den bereits geladenen Jahren ableiten. */
  private applyYearData(response: StatisticsResponse): void {
    this.trend = this.trendYears(response)
      .filter(year => this.yearData.has(year))
      .map(year => {
        const entry = this.yearData.get(year)!;
        return {
          year,
          stock: entry.total.stockAtPeriodEnd,
          isCurrentYear: entry.isCurrentYear
        };
      });
    this.previousResponse = this.yearData.get(this.selectedYear - 1) ?? null;
    this.applyResponse();
  }

  private applyResponse(): void {
    if (!this.response) {
      return;
    }
    this.rows = buildCategoryRows(this.response, this.previousResponse);
  }
}
